import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const PDF_CAPTURE_WIDTH_PX = 820;
const PDF_MARGIN_X_MM = 8;
const PDF_MARGIN_TOP_MM = 14;
const PDF_MARGIN_BOTTOM_MM = 8;
const PDF_SIGNATURE_FOOTER_MM = 22;
const PDF_PAGE_BG = { r: 250, g: 248, b: 245 };
const PAGE_BREAK_GAP_PX = 20;

function pageBodyHeightMm() {
  const pageHeightMm = 297;
  return pageHeightMm - PDF_MARGIN_TOP_MM - PDF_MARGIN_BOTTOM_MM - PDF_SIGNATURE_FOOTER_MM;
}

function pageContentHeightPx() {
  const pageWidthMm = 210;
  const contentWidthMm = pageWidthMm - PDF_MARGIN_X_MM * 2;
  return PDF_CAPTURE_WIDTH_PX * (pageBodyHeightMm() / contentWidthMm);
}

function prepareReceiptPageBreaks(root) {
  const adjustments = [];
  const pageHeightPx = pageContentHeightPx();
  const rootRect = root.getBoundingClientRect();

  const blocks = Array.from(
    root.querySelectorAll(".portal-hero, .receipt-card, .portal-stats, .receipt-terms")
  )
    .map((block) => {
      const rect = block.getBoundingClientRect();
      return {
        block,
        top: rect.top - rootRect.top,
        height: rect.height,
      };
    })
    .sort((a, b) => a.top - b.top);

  let extraOffset = 0;

  blocks.forEach(({ block, top, height }) => {
    if (height >= pageHeightPx) return;

    const adjustedTop = top + extraOffset;
    const bottom = adjustedTop + height;
    const startPage = Math.floor(adjustedTop / pageHeightPx);
    const endPage = Math.floor((bottom - 1) / pageHeightPx);

    if (endPage > startPage) {
      const nextPageStart = (startPage + 1) * pageHeightPx;
      const paddingTop = nextPageStart - adjustedTop + PAGE_BREAK_GAP_PX;
      const currentMargin = parseFloat(getComputedStyle(block).marginTop) || 0;

      adjustments.push({ block, marginTop: block.style.marginTop });
      block.style.marginTop = `${currentMargin + paddingTop}px`;
      extraOffset += paddingTop;
    }
  });

  return adjustments;
}

function restoreReceiptPageBreaks(adjustments) {
  adjustments.forEach(({ block, marginTop }) => {
    block.style.marginTop = marginTop;
  });
}

function waitForLayout() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function drawSignatureFooter(pdf, pageWidth, pageHeight) {
  const footerTop = pageHeight - PDF_MARGIN_BOTTOM_MM - PDF_SIGNATURE_FOOTER_MM;
  const contentLeft = PDF_MARGIN_X_MM;
  const contentWidth = pageWidth - PDF_MARGIN_X_MM * 2;
  const columnGap = 16;
  const columnWidth = (contentWidth - columnGap) / 2;
  const lineY = footerTop + 11;
  const labelY = lineY + 4.5;
  const ink = { r: 28, g: 28, b: 28 };
  const rule = { r: 200, g: 196, b: 190 };

  pdf.setDrawColor(rule.r, rule.g, rule.b);
  pdf.setLineWidth(0.25);
  pdf.line(contentLeft, footerTop + 1.5, contentLeft + contentWidth, footerTop + 1.5);

  const columns = [
    { label: "Applicant Signature", x: contentLeft },
    { label: "Authorized Signatory", x: contentLeft + columnWidth + columnGap },
  ];

  columns.forEach(({ label, x }) => {
    pdf.setDrawColor(ink.r, ink.g, ink.b);
    pdf.setLineWidth(0.35);
    pdf.line(x, lineY, x + columnWidth * 0.85, lineY);

    pdf.setTextColor(ink.r, ink.g, ink.b);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.text(label.toUpperCase(), x, labelY);
  });
}

export async function downloadReceiptPdf(element, filename) {
  if (!element) return;

  element.classList.add("receipt-pdf-export");
  await waitForLayout();

  let pageBreakAdjustments = [];

  try {
    pageBreakAdjustments = prepareReceiptPageBreaks(element);
    await waitForLayout();

    const canvas = await html2canvas(element, {
      scale: 2.5,
      useCORS: true,
      backgroundColor: "#faf8f5",
      logging: false,
      width: PDF_CAPTURE_WIDTH_PX,
      windowWidth: PDF_CAPTURE_WIDTH_PX,
    });

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pageWidth - PDF_MARGIN_X_MM * 2;
    const contentHeight = pageBodyHeightMm();
    const sliceHeightPx = (contentHeight / contentWidth) * canvas.width;

    const sliceCanvas = document.createElement("canvas");
    const sliceCtx = sliceCanvas.getContext("2d");

    let sourceY = 0;
    let pageIndex = 0;

    while (sourceY < canvas.height) {
      if (pageIndex > 0) pdf.addPage();

      pdf.setFillColor(PDF_PAGE_BG.r, PDF_PAGE_BG.g, PDF_PAGE_BG.b);
      pdf.rect(0, 0, pageWidth, pageHeight, "F");

      const slicePx = Math.min(sliceHeightPx, canvas.height - sourceY);
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = slicePx;
      sliceCtx.clearRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      sliceCtx.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        slicePx,
        0,
        0,
        canvas.width,
        slicePx
      );

      const sliceImgData = sliceCanvas.toDataURL("image/png");
      const sliceHeightMm = (slicePx * contentWidth) / canvas.width;

      pdf.addImage(
        sliceImgData,
        "PNG",
        PDF_MARGIN_X_MM,
        PDF_MARGIN_TOP_MM,
        contentWidth,
        sliceHeightMm
      );

      drawSignatureFooter(pdf, pageWidth, pageHeight);

      sourceY += sliceHeightPx;
      pageIndex += 1;
    }

    pdf.save(filename);
  } finally {
    restoreReceiptPageBreaks(pageBreakAdjustments);
    element.classList.remove("receipt-pdf-export");
  }
}
